import {
  EntityLinks,
  Goal,
  GoalFormLinks,
  IconFormNames,
  ID,
  IDPrefixes,
  SortingMode,
  Task,
  Timestamp,
} from '@griply/types';
import {
  createPrefixId,
  getDirectGoalLifeAreaIds,
  isResolvedColor,
} from '@griply/utils';

import { BatchDocuments, WriteBatchFn } from '../batch-documents';
import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';
import { getNewGoalLinkFlags } from './build-created-goal-link-writes';

export type TransformGoalSectionToGoalOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  sectionId: ID;
  goal: Goal;
  // Resolves the source goal's own life areas; a migrated goal records them nowhere else.
  links: EntityLinks;
  isSubGoal?: boolean;
  // The section's tasks, resolved by the caller (each app fetches with its SDK).
  tasks: Task[];
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  db: TDb;
  serverTimestamp: () => TFieldValue;
  writeBatch: WriteBatchFn<TDocRef, TDb>;
  onWriteError: (error: unknown) => void;
};

// Promotes a goal section into a standalone goal (or sub-goal): a new goal doc
// inherits the section's tasks, those tasks are re-pointed to it, and the
// section is removed from the source goal. Source-goal counts are absolute
// here (raw batch update), not the increment deltas used by `updateGoal`.
export const transformGoalSectionToGoal = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb = FirestoreInstance,
>(
  options: TransformGoalSectionToGoalOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TDb
  >,
): Promise<ID | undefined> => {
  const {
    sectionId,
    goal,
    links,
    isSubGoal,
    tasks,
    collection,
    doc,
    db,
    serverTimestamp,
    writeBatch,
    onWriteError,
  } = options;

  const section = goal.sections?.find(({ id }) => id === sectionId);
  if (!section) {
    return undefined;
  }

  const batchDocuments = new BatchDocuments<TDocRef, TDb>(db, writeBatch);
  const goalCollection = collection(db, FirestoreCollections.Goals);
  const taskCollection = collection(db, FirestoreCollections.Tasks);
  const relationshipCollection = collection(
    db,
    FirestoreCollections.Relationships,
  );

  const completedTasks = tasks.filter(({ completedAt }) => !!completedAt);

  // A sub-goal inherits the life area through its parent link; only a top-level goal links the area itself.
  const newGoalLinks: GoalFormLinks = {
    parentIds: isSubGoal ? [goal.id] : [],
    lifeAreaIds: isSubGoal ? [] : getDirectGoalLifeAreaIds(goal, links),
  };

  const newGoalRef = doc(goalCollection);
  const newGoal: Goal = {
    id: newGoalRef.id,
    name: section.name,
    taskCount: tasks.length,
    completedTaskCount: completedTasks.length,
    // A source goal that inherits its own colour leaves this null: the new goal's
    // parent link resolves it the same way.
    color: isResolvedColor(goal.color) ? goal.color : null,
    lifeAreaId: newGoalLinks.lifeAreaIds[0] ?? null,
    // A promoted section starts without a timeframe — it does not inherit the
    // source goal's start date / deadline.
    startDate: null,
    deadline: null,
    iconName: IconFormNames.Target,
    image: null,
    sections: null,
    goalSorting: null,
    metric: null,
    taskSorting: section.tasks,
    roles: goal.roles,
    ancestorIds: null,
    sortingMode: SortingMode.Custom,
    taskSortingMode: SortingMode.Custom,
    metadata: null,
    excludedFromLimits: null,
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    createdAt: serverTimestamp() as unknown as Timestamp,
    updatedAt: serverTimestamp() as unknown as Timestamp,
    ...getNewGoalLinkFlags(newGoalLinks),
  };
  batchDocuments.addDocument({ ref: newGoalRef, data: newGoal });

  const references = [
    ...newGoalLinks.parentIds.map((id) => createPrefixId(id, IDPrefixes.Goal)),
    ...newGoalLinks.lifeAreaIds.map((id) =>
      createPrefixId(id, IDPrefixes.LifeArea),
    ),
  ];
  if (references.length) {
    const relationshipId = createPrefixId(newGoal.id, IDPrefixes.Goal);
    batchDocuments.addDocument({
      ref: doc(relationshipCollection, relationshipId),
      data: {
        id: relationshipId,
        references,
        roles: { owner: goal.roles.owner, all: [goal.roles.owner] },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
    });
  }

  tasks.forEach((task) => {
    batchDocuments.updateDocument({
      ref: doc(taskCollection, task.id),
      data: {
        goalIds: [newGoal.id],
        goalId: newGoal.id,
        updatedAt: serverTimestamp(),
      },
    });
  });

  batchDocuments.updateDocument({
    ref: doc(goalCollection, goal.id),
    data: {
      taskCount: Math.max((goal.taskCount ?? 0) - tasks.length, 0),
      completedTaskCount: Math.max(
        (goal.completedTaskCount ?? 0) - completedTasks.length,
        0,
      ),
      sections:
        goal.sections?.filter((section) => section.id !== sectionId) ?? null,
      updatedAt: serverTimestamp(),
    },
  });

  detachWrite(batchDocuments.commit(), onWriteError);

  return newGoalRef.id;
};
